import { createContext, useContext, useState } from "react";
import { Modal, ModalProps } from "./Modal";
import { v1 } from "uuid";
import { ConfirmModal, ConfirmModalProps } from "./ConfirmModal";

export type ModalProviderState = {
    openModal: (modal: Omit<ModalProps, "onClose" | "open">, onClose?: () => void) => void;
    openConfirmModal: (modal: Omit<ConfirmModalProps, "onClose" | "open">, onClose?: () => void) => void;
}

const initialState: ModalProviderState = {
    openModal: () => { },
    openConfirmModal: () => { },
}

const ModalProviderContext = createContext<ModalProviderState>(initialState);

export type ModalProviderProps = {
    children: React.ReactNode;
}

export const ModalProvider = ({ children }: ModalProviderProps) => {
    const [modals, setModals] = useState<ModalProps[]>([]);
    const [confirmModals, setConfirmModals] = useState<ConfirmModalProps[]>([]);

    const openModal = (modal: Omit<ModalProps, "onClose" | "open">, onClose?: () => void) => {
        const newModal: ModalProps = {
            ...modal,
            open: true,
            onClose: () => {
                setModals((prev) => prev.filter((m) => m !== newModal));
                if (onClose) onClose();
            }
        };

        setModals((prev) => [...prev, newModal]);
    };

    const openConfirmModal = (modal: Omit<ConfirmModalProps, "onClose" | "open">, onClose?: () => void) => {
        const newModal: ConfirmModalProps = {
            ...modal,
            open: true,
            onClose: () => {
                setConfirmModals((prev) => prev.filter((m) => m !== newModal));
                if (onClose) onClose();
            }
        };

        setConfirmModals((prev) => [...prev, newModal]);
    }

    return (
        <ModalProviderContext.Provider value={{ openModal, openConfirmModal }}>
            {children}

            {modals.map((modal: ModalProps) => <Modal key={v1()} {...modal} />)}

            {confirmModals.map((modal: ConfirmModalProps) => <ConfirmModal key={v1()} {...modal} />)}
        </ModalProviderContext.Provider>
    );
}

ModalProvider.useModal = () => {
    const context = useContext(ModalProviderContext);

    if (!context) {
        throw new Error("useModal must be used within a ModalProvider");
    }

    return context;
};